/* eslint-disable react/jsx-props-no-spreading */
import React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useRouter } from 'next/router'
import { useEnrollmentData, useLinkEnrollment } from './hooks'
import ErrorDisplay from './components/ErrorDisplay'
import ReasonInfoCard from './components/ReasonInfoCard'
import { Spinner, Text } from '@geist-ui/react'

export default function SignupComplete({
  alreadyEnrolled,
  differentEmail,
  createdNewAccount,
}) {
  // If the user is already enrolled, show the reason info card
  if (alreadyEnrolled) {
    if (createdNewAccount) {
      return (
        <ReasonInfoCard
          reasonInfo={{
            title: 'Account already enrolled',
            description:
              'Your account is already enrolled. Check your email to set your password, then log in to continue enrollment.',
          }}
          showLoginButton={true}
        />
      )
    }
    if (differentEmail) {
      return (
        <ReasonInfoCard
          reasonInfo={{
            title: 'Account already enrolled',
            description:
              'This service address is already registered with another user.',
          }}
          showLoginButton={true}
        >
          <Text>Options:</Text>
          <ul>
            <li>Log in with the original email on file.</li>
            <li>Use “Forgot Password” to reset your password.</li>
            <li>
              If you do not receive the reset email within 5–10 minutes, contact
              support.
            </li>
          </ul>
        </ReasonInfoCard>
      )
    }

    return (
      <ReasonInfoCard
        reasonInfo={{
          title: 'Account already enrolled',
          description:
            'Your account is already enrolled. You can manage coverage and file claims below.',
        }}
        showLoginButton={true}
      />
    )
  }

  const { user, isAuthenticated, isLoading: authLoading } = useAuth0()
  const router = useRouter()

  // Load enrollment data from sessionStorage
  const {
    enrollmentData,
    flowType,
    error: enrollmentDataError,
    setError: setEnrollmentDataError,
  } = useEnrollmentData()

  // Link enrollment hook
  const {
    isLinking,
    error: linkError,
    enrollmentSubmitted: linkSubmitted,
    setError: setLinkError,
    setIsLinking: setLinkProcessing,
  } = useLinkEnrollment({
    flowType,
    enrollmentData,
    user,
    isAuthenticated,
    authLoading,
  })

  // Determine which error to show
  const error = linkError || enrollmentDataError
  const isProcessing = authLoading || isLinking

  // Determine completion state based on flow type
  // For complete: show completion when enrollment is submitted
  // For link: completion handled by redirect to /success, but check for consistency
  // For claim: show completion when claim is submitted
  const isCompleted = flowType === 'link' && linkSubmitted

  // Show loading spinner while checking auth or processing
  if (isProcessing) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '700px',
          width: '100%',
        }}
      >
        <Spinner />
      </div>
    )
  }

  // If the user is not authenticated, redirect to the auth page
  if (!authLoading && !isAuthenticated) {
    return (
      <ReasonInfoCard
        reasonInfo={{
          title: 'Account already enrolled',
          description:
            'This email is already registered. Please log in to continue enrollment for this new address. We saved your enrollment details so we can resume after you log in.',
        }}
        showLoginButton={true}
      />
    )
  }

  // Show error state
  if (error) {
    // Determine which error handler to use based on error source
    const handleClearError = () => {
      if (linkError) {
        setLinkError(null)
      }
      if (enrollmentDataError) {
        setEnrollmentDataError(null)
      }
    }

    const handleClearProcessing = () => {
      if (linkError) {
        setLinkProcessing(false)
      }
    }

    return (
      <ErrorDisplay
        error={error}
        title="Enrollment Processing Error"
        flowType={flowType}
        onClearError={handleClearError}
        onClearProcessing={handleClearProcessing}
      />
    )
  }

  if (isCompleted) {
    // Redirect to Success page using Next.js router
    router.push('/success')
    return
  }

  // Fallback
  return (
    <ReasonInfoCard
      reasonInfo={{
        title: 'Enrollment already linked',
        description:
          'Your account is already enrolled. You can manage coverage and file claims below.',
      }}
      showLoginButton={true}
    />
  )
}
